import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { AmplitudeService } from "@dcc/shared/amplitude";
import { ApplicationIdList } from "@dcc/shared/configs/vo-applications";
import { ComponentStore } from "@ngrx/component-store";
import { distinctUntilKeyChanged, Observable, pipe, tap } from "rxjs";
import { filter, map } from "rxjs/operators";
import { fromEmailReferrer } from "./amplitude.events";
import { TELEMETRY_CONFIGS } from "./tokens";

@Injectable({ providedIn: "root" })
export class UserTelemetryService extends ComponentStore<never> {
    private amplitude = inject(AmplitudeService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private config = inject(TELEMETRY_CONFIGS);

    private application_identifier = "applicationId";

    constructor() {
        super();

        if (this.config && this.config.applicationIdentifier) {
            this.application_identifier = this.config.applicationIdentifier;
        }

        // initialize
        this.logUserVisitsEffect(this.routeData());
    }

    private readonly logUserVisitsEffect = this.effect<{ applicationId: string; referrer: string }>(
        pipe(
            distinctUntilKeyChanged("applicationId"),
            filter(({ applicationId }) => applicationId !== null && ApplicationIdList.includes(applicationId)),
            tap(({ referrer }) => {
                if (referrer) {
                    this.amplitude.trackEvent(fromEmailReferrer());
                    this.router.navigate([], {
                        queryParams: { referrer: null },
                        queryParamsHandling: "merge",
                        relativeTo: this.route,
                        replaceUrl: true
                    });
                }
            })
        )
    );

    private routeData(): Observable<{ applicationId: string; referrer: string }> {
        return this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => {
                let child = this.route?.firstChild;

                if (child) {
                    while (child.firstChild) {
                        child = child.firstChild;
                    }

                    return {
                        applicationId: child.snapshot.data[this.application_identifier] ?? null,
                        referrer: child?.snapshot?.queryParams["referrer"] ?? null
                    };
                }

                return { applicationId: null, referrer: null };
            })
        );
    }
}
